import { Fragment, useEffect, useState } from "react";
import type { NoteDTO } from "../../../shared/types";
import { List, ListRow, listRowCls } from "../../components/List";
import { confirmDialog } from "../../components/Dialog";
import { Button, ErrorText, inputCls, PageHeader, timeAgo } from "../../components/ui";
import { del, get, put } from "../../lib/api";

const ORIGIN: Record<string, string> = { told: "they said so", own: "the agent added it", suggested: "a suggestion they accepted" };

/** One person's note, opened for support. Every edit here is in the audit log under that person. */
function PersonNote({ note, onChanged }: { note: NoteDTO; onChanged: () => Promise<void> }) {
  const [text, setText] = useState(note.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(note.text), [note.currentVersionId, note.text]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={listRowCls}>
        <div className="mb-1 text-xs font-medium text-muted">In their words — only edit this for them, and tell them you did</div>
        <textarea className={`${inputCls} min-h-32`} value={text} onChange={(e) => setText(e.target.value)} />
        <div className="mt-2 flex justify-end">
          <Button variant="primary" disabled={busy || text === note.text} onClick={() => run(() => put(`/api/notes/${note.userId}`, { text }))}>
            Save a new version
          </Button>
        </div>
      </div>
      <div className={`${listRowCls} bg-fill-subtle text-xs font-medium text-muted`}>Learned by agents · {note.items.length}</div>
      {note.items.map((i) => (
        <ListRow
          key={i.id}
          title={<span className="min-w-0 text-sm break-words">{i.text}</span>}
          trailing={
            <Button variant="danger" size="sm" disabled={busy} onClick={async () => (await confirmDialog({ title: "Delete this item?", body: "Agents stop reading it from their next turn.", confirm: "Delete", danger: true })) && run(() => del(`/api/notes/${note.userId}/items/${i.id}`))}>
              Delete
            </Button>
          }
        >
          <span className="mt-1 block text-xs text-faint">
            {ORIGIN[i.origin]}
            {i.agentName ? ` · ${i.agentName}` : ""} · {timeAgo(i.createdAt)}
          </span>
        </ListRow>
      ))}
      {note.items.length === 0 && <div className={`${listRowCls} text-sm text-muted`}>Nothing learned yet.</div>}
      {error && (
        <div className={listRowCls}>
          <ErrorText error={error} />
        </div>
      )}
    </>
  );
}

/** Every person's note: the page an agent reads before it works for them, and an admin's way in when they need help. */
export function AdminNotes() {
  const [notes, setNotes] = useState<NoteDTO[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => get<NoteDTO[]>("/api/notes").then(setNotes, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <PageHeader title="User notes" subtitle="The page every agent working for someone is handed. People write their own on About me; edits here are recorded." />
      <ErrorText error={error} />
      {notes && (
        <List empty="No people yet.">
          {notes.map((n) => (
            <Fragment key={n.userId}>
              <div className={listRowCls}>
                <button className="flex w-full items-center gap-3 text-left" onClick={() => setOpen(open === n.userId ? null : n.userId)}>
                  <span className="min-w-0 flex-1 truncate font-medium">{n.userName}</span>
                  <span className="shrink-0 text-xs text-faint">
                    {n.chars} / {n.maxChars} chars · {n.items.length} learned · {n.versions} version{n.versions === 1 ? "" : "s"}
                    {n.updatedAt ? ` · updated ${timeAgo(n.updatedAt)}` : ""}
                  </span>
                </button>
                {open !== n.userId && (n.text || n.items.length > 0) && (
                  <p className="mt-1.5 line-clamp-2 text-sm text-muted">{n.text || n.items.map((i) => i.text).join(" · ")}</p>
                )}
              </div>
              {open === n.userId && <PersonNote note={n} onChanged={load} />}
            </Fragment>
          ))}
        </List>
      )}
    </div>
  );
}
